import axios from "axios";

const BASE_URL = "https://gg-api.nationtonation.ca/api/products";

// Type definitions
// Product creation payload
// Product creation payload
interface ProductCreateData {
  name: string;
  description: string;
  category_id: number;
  brand_id: number;
  status: "active" | "inactive";
  min_order_qty: string;          // ✅ product-level MOQ
  order_multiple?: string;        // ✅ NEW: product-level pack size
  base_price: string;
  image: string;
  master_sku: string;
  stock_quantity: string;
  variants: {
    id?: number;
    variant_name: string;
    price: string;
    stock_quantity: string;
    sku: string;
    image: string;
    min_order_qty?: string;       // ✅ variant-level MOQ
    order_multiple?: string;      // ✅ NEW: variant-level pack size
    selected_options?: Record<string, string>;
  }[];
  options?: {
    name: string;
    values: string[];
    isRequired: boolean;
  }[];
}

interface ProductUpdateData extends ProductCreateData {
  id?: string;
}

interface Variant {
  id?: number;
  variant_name: string;
  price: string;
  stock_quantity: string;
  sku: string;
  image: string;
  min_order_qty?: string;         // ✅ keep
  order_multiple?: string;        // ✅ NEW
}


// Fetch all products
export const fetchProducts = async (status: "active" | "inactive" = "active") => {
  try {
    const response = await axios.get(`${BASE_URL}?status=${status}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error;
  }
};





// Fetch a single product by ID
export const fetchProductById = async (productId: string) => {
  if (!productId || isNaN(Number(productId))) {
    throw new Error("Invalid product ID");
  }

  try {
    const response = await axios.get(`${BASE_URL}/${productId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching product with ID ${productId}:`, error);
    throw error;
  }
};
// Create a new product with inventory support
export const createProduct = async (productData: ProductCreateData) => {
  try {
    const payload = {
      ...productData,
      category_id: Number(productData.category_id),
      base_price: parseFloat(productData.base_price),
      stock_quantity: parseInt(productData.stock_quantity),
      min_order_qty: parseInt(productData.min_order_qty),                // ✅ product MOQ
      order_multiple: productData.order_multiple
        ? parseInt(productData.order_multiple)
        : 1,                                                             // ✅ NEW: product pack size (default 1)
      variants: productData.variants.map((v) => ({
        ...v,
        price: parseFloat(v.price),
        stock_quantity: parseInt(v.stock_quantity),
        min_order_qty: v.min_order_qty ? parseInt(v.min_order_qty) : 1,  // ✅ variant MOQ
        order_multiple: v.order_multiple ? parseInt(v.order_multiple) : 1, // ✅ NEW: variant pack size (default 1)
      })),
    };

    const response = await axios.post(BASE_URL, payload, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Error creating product:", error);
    throw error;
  }
};


export const updateProduct = async (
  productId: string,
  productData: ProductUpdateData
) => {
  try {
    const payload = {
      ...productData,
      category_id: Number(productData.category_id),
      base_price: parseFloat(productData.base_price),
      stock_quantity: parseInt(productData.stock_quantity),
      min_order_qty: parseInt(productData.min_order_qty),                // ✅ product MOQ
      order_multiple: productData.order_multiple
        ? parseInt(productData.order_multiple)
        : 1,                                                             // ✅ NEW: product pack size
      variants: productData.variants.map((v) => ({
        ...v,
        price: parseFloat(v.price),
        stock_quantity: parseInt(v.stock_quantity),
        min_order_qty: v.min_order_qty ? parseInt(v.min_order_qty) : 1,  // ✅ variant MOQ
        order_multiple: v.order_multiple ? parseInt(v.order_multiple) : 1, // ✅ NEW: variant pack size
      })),
    };

    const response = await axios.put(`${BASE_URL}/${productId}`, payload, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Error updating product:", error);
    throw error;
  }
};



// Delete a product
export const deleteProduct = async (productId: string) => {
  try {
    const response = await axios.delete(`${BASE_URL}/${productId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting product:", error);
    throw error;
  }
};

// Customer-specific operations
export const getProductsForCustomer = async (customerId: number) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/customer?customerId=${customerId}`,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching customer products:", error);
    throw error;
  }
};

// Pricing operations
export const setCustomPricing = async (data: {
  customerId: number;
  productId: number;
  customPrice: number;
  variantId?: number;
}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/set-price`,
      data,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error setting custom pricing:", error);
    throw error;
  }
};

// Variant operations
export const deleteVariantOption = async (id: number) => {
  try {
    const response = await axios.delete(`${BASE_URL}/variant-options/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting variant option:", error);
    throw error;
  }
};

export const deleteVariantValue = async (id: number) => {
  try {
    const response = await axios.delete(`${BASE_URL}/variant-value/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting variant value:", error);
    throw error;
  }
};




// Category operations
export const fetchProductCategories = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/categories`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

// Bulk pricing operations
export const setBulkCustomPricing = async (data: {
  customerIds: number[];
  productId: number;
  customPrice: number;
  variantId?: number;
}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/set-bulk-price`,
      data,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error setting bulk pricing:", error);
    throw error;
  }
};
// In productApi.ts
export const createCategory = async (categoryData: { name: string }) => {
  try {
    const response = await axios.post(`${BASE_URL}/categories`, categoryData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}` // Add auth header
      }
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error creating category:', error);
    throw error;
  }
};


export const updateVariantStock = async (
  productId: number,
  variantId: number | null,
  stockQuantity: number
) => {
  const response = await axios.put(
    `https://gg-api.nationtonation.ca/api/products/update-stock`,
    {
      product_id: productId,
      variant_id: variantId,
      stock_quantity: stockQuantity
    },
    {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    }
  );
  return response.data;
};

export const fetchBrands = async () => {
  const response = await axios.get(`${BASE_URL}/brands`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return response.data;
};

export const createBrand = async (brandData: {
  name: string;
  logo?: string;
  description?: string;
}) => {
  const response = await axios.post(`${BASE_URL}/brands`, brandData, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return response.data;
};

// Set custom tiered pricing for a customer or subaccount
export const setCustomerTieredPricing = async (data: {
  customerId: number;
  productId: number;
  tiers: {
    minQuantity: number;
    pricePerUnit: number;
    variantId?: number;
  }[];
  subaccountId?: number;
}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/set-customer-tiered-price`,
      data,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error setting tiered pricing:", error);
    throw error;
  }
};
interface Brand {
  id: number;
  name: string;
  logo?: string;
  description?: string;
}

export const updateBrand = async (
  id: number,
  brandData: {
    name: string;
    logo?: string;
    description?: string;
  }
): Promise<Brand> => {
  try {
    const response = await axios.put(`${BASE_URL}/brands/${id}`, brandData, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating brand:", error);
    throw error;
  }
};
export const deleteBrand = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/brands/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
  } catch (error) {
    console.error("Error deleting brand:", error);
    throw error;
  }
};

export const deleteCategory = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/categories/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
  } catch (error) {
    console.error("Error deleting category:", error);
    throw error;
  }
};

export const updateCategory = async (id: number, payload: { name: string }) => {
  try {
    const { data } = await axios.put(
      `${BASE_URL}/categories/${id}`,
      payload,
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return data as { id: number; name: string };
  } catch (error) {
    console.error("Error updating category:", error);
    throw error;
  }
};
